import React, { useReducer, useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { History } from 'history';

import { FlowAction, FunnelState, FunnelType } from 'scenes/common/types';
import { isPaymentFunnel, route } from 'scenes/common/helpers/route';
import { previousPage } from 'scenes/common/actions';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dispatch = (action: FlowAction<any, any>) => void;
type ProviderProps<FV, F, S, T extends FunnelState<F, S>> = {
  children: React.ReactNode;
  initialFunnelState: T;
  reducer: (state: T, action: FlowAction<FV, S>) => T;
};

const StateContext = React.createContext<
  FunnelState<unknown, unknown> | undefined
>(undefined);
const DispatchContext = React.createContext<Dispatch | undefined>(undefined);

function ContextProvider<FV, F, S, T extends FunnelState<F, S>>({
  children,
  initialFunnelState,
  reducer,
}: ProviderProps<FV, F, S, T>) {
  const [state, dispatch] = useReducer(reducer, initialFunnelState);
  const history: History = useHistory();
  const { productType } = useParams<{ productType: FunnelType }>();

  useEffect(() => {
    if (state.flow && state.step && !isPaymentFunnel(productType)) {
      history.push(route(productType, state.step), {
        flow: state.flow,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, state.step, state.flow, productType]);

  useEffect(() => {
    if (history.action === 'POP' && !isPaymentFunnel(productType)) {
      dispatch({ ...previousPage(), sourceStep: state.step });
    }
  }, [history.action, state, productType]);

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

function useFunnelState<T extends FunnelState<unknown, unknown>>() {
  const context = useContext(StateContext) as T;
  if (context === undefined) {
    throw new Error('useFunnelState must be used within a StateProvider');
  }
  return context;
}

function useQuoteFlowDispatch() {
  const context = useContext(DispatchContext);
  if (context === undefined) {
    throw new Error(
      'useQuoteFlowDispatch must be used within a DispatchProvider'
    );
  }
  return context;
}

export { ContextProvider, useFunnelState, useQuoteFlowDispatch };
